import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { FeedIcon } from './FeedIcon';
import { FeedKeyTooltip } from './FeedKeyTooltip';

import type { ReactNode } from 'react';

// How an update's app mark shows it can be cleared, while the design is chosen
type UpdateCheckStyle = 'swap' | 'badge' | 'overlay';
const UPDATE_CHECK_STYLE = 'badge' as UpdateCheckStyle;

type FeedIconCheckProps = {
  title: string;
  // `box` is a to-do's or a draft's checkbox; `mark` is an update's app mark, which the user clears once read
  kind: 'box' | 'mark';
  // What a click does, in the tooltip: "Mark done", or "Mark as read" for an update
  label: string;
  failed: boolean;
  // Held ticked while the row strikes its title and leaves
  ticked: boolean;
  onCheck: () => void;
  children: ReactNode;
};

const TILE_BOX =
  'absolute left-0 top-[calc((var(--ac-row-icon)-var(--ac-row-tile))/2)] flex size-[var(--ac-row-tile)] items-center justify-center';

const SHOWN_ON_POINT =
  'opacity-0 transition-opacity duration-[120ms] group-hover/check:opacity-100 group-focus-visible/check:opacity-100 motion-reduce:transition-none';

// A collapsed row's icon is how the user checks the card off, as a to-do list's box is: pointing at it shows the tick,
// and a click marks the card done, or an update read. It sits above the title's full-row button, so the click does
// not open the card
export const FeedIconCheck = ({
  title,
  kind,
  label,
  failed,
  ticked,
  onCheck,
  children,
}: FeedIconCheckProps) => (
  <FeedKeyTooltip label={label} keys={['d']}>
    <button
      type="button"
      aria-label={`${label}: ${title}`}
      aria-pressed={ticked}
      data-feed-icon-check={kind}
      className="group/check relative z-[1] flex rounded-[var(--ac-row-tile-radius)] focus-ring"
      onClick={ticked ? undefined : onCheck}
    >
      <span
        className={cn(
          'flex',
          kind === 'mark' &&
            UPDATE_CHECK_STYLE !== 'badge' &&
            'transition-opacity duration-[120ms] group-hover/check:opacity-0 group-focus-visible/check:opacity-0 motion-reduce:transition-none',
          kind === 'mark' &&
            UPDATE_CHECK_STYLE === 'overlay' &&
            'group-hover/check:opacity-30 group-focus-visible/check:opacity-30',
          kind === 'mark' && ticked && 'opacity-0'
        )}
      >
        <FeedIcon failed={failed}>{children}</FeedIcon>
      </span>
      {kind === 'box' ? (
        // The tick drawn inside the square, in the square's own colour
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none',
            TILE_BOX,
            'text-foreground-subtle',
            ticked ? 'opacity-100' : SHOWN_ON_POINT
          )}
        >
          <Icons.check className="size-[calc(10*var(--ac-u))] [stroke-width:3]" />
        </span>
      ) : UPDATE_CHECK_STYLE === 'badge' ? (
        // A small round tick at the mark's foot, where a to-do's app badge sits
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute bottom-[calc(-2*var(--ac-u))] left-[calc(var(--ac-row-tile)-11*var(--ac-u))] flex size-[calc(14*var(--ac-u))] items-center justify-center rounded-full bg-background text-foreground-subtle ring-1 ring-border-strong',
            ticked ? 'opacity-100' : SHOWN_ON_POINT
          )}
        >
          <Icons.check className="size-[calc(9*var(--ac-u))] [stroke-width:3]" />
        </span>
      ) : (
        // The mark gives way to a round tick, or a tick over the faded mark
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none',
            TILE_BOX,
            ticked ? 'opacity-100' : SHOWN_ON_POINT
          )}
        >
          <span
            className={cn(
              'flex items-center justify-center text-foreground-subtle',
              UPDATE_CHECK_STYLE === 'swap' &&
                'size-[calc(18*var(--ac-u))] rounded-full ring-1 ring-border-strongest'
            )}
          >
            <Icons.check className="size-[calc(11*var(--ac-u))] [stroke-width:2.75]" />
          </span>
        </span>
      )}
    </button>
  </FeedKeyTooltip>
);
